import type { genericIcon } from '../../ui/icons';

/**
 * Haven Isle content (research 02 §7, scaled down per §11.2): research
 * projects, workshop recipes, dispatch missions and crew, garden crops, and
 * the Haven level curve. Timers are real time.
 */

export type Glyph = Parameters<typeof genericIcon>[2];

// ================================================================ research

/** Effects other systems can read through `havenBonus(account, key)`. */
export type HavenBonusKey =
  | 'potionHeal'
  | 'lifeEnergyCost'
  | 'battleItemLimit'
  | 'temperSilver'
  | 'crewSlots'
  | 'craftSpeed'
  | 'gardenYield'
  | 'lifeRareChance'
  | 'dispatchSuccess';

export interface ResearchLevel {
  silver: number;
  items: Record<string, number>;
  minutes: number;
}

export interface ResearchDef {
  id: string;
  name: string;
  tier: 1 | 2 | 3;
  description: string;
  key: HavenBonusKey;
  /** Bonus per completed level (fractions for percentages). */
  perLevel: number;
  /** Human-readable effect at a given level. */
  effect: (level: number) => string;
  levels: ResearchLevel[];
  requires?: { id: string; level: number }[];
  /** Minimum Haven level to start. */
  havenLevel: number;
  icon: { hue: number; glyph: Glyph };
}

const pct = (v: number) => `${Math.round(v * 100)}%`;

export const RESEARCH: ResearchDef[] = [
  {
    id: 'improved_salves', name: 'Improved Salves', tier: 1, havenLevel: 1, key: 'potionHeal', perLevel: 0.1,
    description: 'Steep the draughts longer and strain them twice.',
    effect: (l) => `Healing potions restore +${pct(0.1 * l)} more HP`,
    icon: { hue: 0xff5a5a, glyph: 'buff' },
    levels: [
      { silver: 300, items: { herb: 8 }, minutes: 3 },
      { silver: 800, items: { herb: 16, herb_rare: 2 }, minutes: 8 },
      { silver: 1600, items: { herb: 24, herb_rare: 5 }, minutes: 15 },
    ],
  },
  {
    id: 'efficient_tools', name: 'Efficient Tools', tier: 1, havenLevel: 1, key: 'lifeEnergyCost', perLevel: 0.1,
    description: 'Balanced hafts and whetted edges for every gatherer.',
    effect: (l) => `Life skills cost ${pct(0.1 * l)} less Life Energy`,
    icon: { hue: 0x9be07a, glyph: 'fist' },
    levels: [
      { silver: 300, items: { timber: 8, ore_raw: 4 }, minutes: 3 },
      { silver: 800, items: { timber: 14, ore_raw: 10 }, minutes: 8 },
      { silver: 1600, items: { timber: 20, timber_rare: 2, ore_rare: 2 }, minutes: 15 },
    ],
  },
  {
    id: 'dispatch_logistics', name: 'Dispatch Logistics', tier: 1, havenLevel: 1, key: 'crewSlots', perLevel: 1,
    description: 'Bunks, ledgers and a second skiff for the harbour.',
    effect: (l) => `+${l} crew member${l === 1 ? '' : 's'} for dispatches`,
    icon: { hue: 0x7ad8ff, glyph: 'dash' },
    levels: [
      { silver: 500, items: { timber: 12, hide: 6 }, minutes: 5 },
      { silver: 1500, items: { timber: 20, hide: 12, fish: 8 }, minutes: 12 },
    ],
  },
  {
    id: 'quartermaster', name: 'Quartermaster', tier: 2, havenLevel: 3, key: 'battleItemLimit', perLevel: 1,
    description: 'Padded bandoliers let you carry one more of everything.',
    effect: (l) => `+${l} use of each battle item per instance`,
    requires: [{ id: 'improved_salves', level: 1 }],
    icon: { hue: 0xffb040, glyph: 'shield' },
    levels: [{ silver: 1500, items: { hide: 10, timber: 10, relic_fragment: 6 }, minutes: 12 }],
  },
  {
    id: 'journeyman_benches', name: 'Journeyman Benches', tier: 2, havenLevel: 3, key: 'craftSpeed', perLevel: 0.15,
    description: 'Proper vices, clamps and a kiln that holds its heat.',
    effect: (l) => `Workshop crafts ${pct(0.15 * l)} faster`,
    requires: [{ id: 'efficient_tools', level: 1 }],
    icon: { hue: 0xd0a060, glyph: 'impact' },
    levels: [
      { silver: 900, items: { timber: 16, ore_raw: 10 }, minutes: 8 },
      { silver: 2000, items: { timber_rare: 3, ore_rare: 3 }, minutes: 15 },
    ],
  },
  {
    id: 'fertile_terraces', name: 'Fertile Terraces', tier: 2, havenLevel: 3, key: 'gardenYield', perLevel: 0.25,
    description: 'Terraced beds, relic-dust compost and a rain cistern.',
    effect: (l) => `Garden harvests +${pct(0.25 * l)}`,
    requires: [{ id: 'improved_salves', level: 1 }],
    icon: { hue: 0x6ad060, glyph: 'star' },
    levels: [
      { silver: 900, items: { herb: 16, relic_fragment: 4 }, minutes: 8 },
      { silver: 2000, items: { herb_rare: 4, relic_fragment: 10 }, minutes: 15 },
    ],
  },
  {
    id: 'forge_rites', name: 'Forge Rites', tier: 3, havenLevel: 5, key: 'temperSilver', perLevel: 0.05,
    description: 'Old smithing rites that coax more from less coin.',
    effect: (l) => `Tempering costs ${pct(0.05 * l)} less silver`,
    requires: [{ id: 'quartermaster', level: 1 }],
    icon: { hue: 0xff7a3a, glyph: 'meteor' },
    levels: [
      { silver: 2500, items: { ore_rare: 3, relic_rare: 1 }, minutes: 15 },
      { silver: 5000, items: { ore_rare: 6, relic_rare: 2 }, minutes: 25 },
    ],
  },
  {
    id: 'surveyors_lore', name: "Surveyor's Lore", tier: 3, havenLevel: 5, key: 'lifeRareChance', perLevel: 0.03,
    description: 'Field journals mapping where the rare things grow.',
    effect: (l) => `Life skills find rare materials +${pct(0.03 * l)} more often`,
    requires: [{ id: 'efficient_tools', level: 2 }],
    icon: { hue: 0xc070ff, glyph: 'orb' },
    levels: [
      { silver: 2000, items: { relic_fragment: 12, herb_rare: 2 }, minutes: 15 },
      { silver: 4000, items: { relic_rare: 2, fish_rare: 2 }, minutes: 25 },
    ],
  },
  {
    id: 'navigators_charts', name: "Navigator's Charts", tier: 3, havenLevel: 5, key: 'dispatchSuccess', perLevel: 0.1,
    description: 'Tide tables and reef soundings for every crew.',
    effect: (l) => `Dispatch success +${pct(0.1 * l)}`,
    requires: [{ id: 'dispatch_logistics', level: 1 }],
    icon: { hue: 0x5ab8e0, glyph: 'spiral' },
    levels: [
      { silver: 2000, items: { fish: 12, timber: 12 }, minutes: 15 },
      { silver: 4000, items: { fish_rare: 2, timber_rare: 2 }, minutes: 25 },
    ],
  },
];

export const RESEARCH_BY_ID: Record<string, ResearchDef> = Object.fromEntries(RESEARCH.map((r) => [r.id, r]));

// ================================================================ workshop

export type RecipeCategory = 'potion' | 'grenade' | 'bomb' | 'utility' | 'material';
export const CATEGORY_NAMES: Record<RecipeCategory, string> = { potion: 'Potions', grenade: 'Grenades', bomb: 'Bombs', utility: 'Utility', material: 'Materials' };

/** Accepts any mix of the listed materials (consumed from the largest stacks first). */
export interface MaterialPool {
  ids: string[];
  count: number;
  label: string;
}

export interface RecipeDef {
  id: string;
  category: RecipeCategory;
  out: { id: string; count: number };
  items: Record<string, number>;
  pools?: MaterialPool[];
  silver: number;
  /** Seconds per batch before research. */
  seconds: number;
  havenLevel: number;
}

export const COMMON_MATS = ['herb', 'timber', 'ore_raw', 'fish', 'hide', 'relic_fragment'];
export const RARE_MATS = ['herb_rare', 'timber_rare', 'ore_rare', 'fish_rare', 'relic_rare'];
const anyCommon = (count: number): MaterialPool => ({ ids: COMMON_MATS, count, label: 'Any common life-skill material' });
const anyRare = (count: number): MaterialPool => ({ ids: RARE_MATS, count, label: 'Any rare life-skill material' });

const r = (id: string, category: RecipeCategory, out: number, items: Record<string, number>, silver: number, seconds: number, havenLevel = 1, pools?: MaterialPool[]): RecipeDef => ({
  id,
  category,
  out: { id, count: out },
  items,
  silver,
  seconds,
  havenLevel,
  pools,
});

export const RECIPES: RecipeDef[] = [
  r('potion_small', 'potion', 3, { herb: 6 }, 60, 20),
  r('potion_major', 'potion', 3, { herb: 10, herb_rare: 1 }, 160, 45),
  r('potion_elixir', 'potion', 2, { herb: 12, herb_rare: 3, fish_rare: 1 }, 420, 120, 3),
  r('potion_panacea', 'potion', 2, { herb: 8, fish: 4 }, 120, 40),
  r('potion_protect', 'potion', 2, { herb: 6, hide: 4 }, 120, 40),
  r('potion_timestop', 'potion', 1, { herb_rare: 2, relic_rare: 1 }, 600, 180, 4),
  r('grenade_flame', 'grenade', 3, { timber: 10, ore_raw: 2 }, 150, 60),
  r('grenade_frost', 'grenade', 3, { fish: 6, ore_raw: 3 }, 150, 60),
  r('grenade_flash', 'grenade', 3, { herb: 6, relic_fragment: 3 }, 150, 60, 2),
  r('grenade_clay', 'grenade', 3, { relic_fragment: 4, timber: 4 }, 120, 60),
  r('grenade_whirl', 'grenade', 2, { timber: 8, timber_rare: 2 }, 320, 120, 3),
  r('grenade_dark', 'grenade', 2, { relic_fragment: 6, herb_rare: 1 }, 320, 120, 3),
  r('bomb_destruction', 'bomb', 2, { ore_raw: 12, ore_rare: 2 }, 320, 120, 2),
  r('bomb_corrosive', 'bomb', 3, { ore_raw: 8, herb: 4 }, 150, 60),
  r('bomb_sleep', 'bomb', 3, { herb: 8, fish: 3 }, 150, 60, 2),
  r('flag_march', 'utility', 2, { timber: 6, hide: 6 }, 150, 60, 2),
  r('campfire_kit', 'utility', 2, { timber: 10, hide: 2 }, 100, 45),
  r('fusion_alloy', 'material', 5, {}, 400, 300, 2, [anyCommon(30), anyRare(1)]),
  r('catalyst', 'material', 1, { relic_rare: 1 }, 1500, 600, 4, [anyRare(3)]),
];

export const RECIPE_BY_ID: Record<string, RecipeDef> = Object.fromEntries(RECIPES.map((x) => [x.id, x]));
export const MAX_CRAFT_QUEUE = 4;
export const MAX_BATCHES = 10;
/** Chance a finished order comes out as a Great Success (+50% items). */
export const GREAT_CRAFT_CHANCE = 0.1;

// ================================================================ dispatch

export type Hazard = 'storm' | 'fog' | 'beasts' | 'reef' | 'cold';
export const HAZARDS: Record<Hazard, { name: string; color: string }> = {
  storm: { name: 'Storm', color: '#8a9cff' },
  fog: { name: 'Fog', color: '#b7c0c8' },
  beasts: { name: 'Beasts', color: '#e0875a' },
  reef: { name: 'Reef', color: '#4fc2b0' },
  cold: { name: 'Frost', color: '#9fe0ff' },
};

export interface CrewDef {
  name: string;
  trait: string;
  counters: Hazard;
  blurb: string;
}

export const CREW: Record<string, CrewDef> = {
  Tam: { name: 'Tam', trait: 'Stormhand', counters: 'storm', blurb: 'Hauled nets through squalls since she could walk.' },
  Oriel: { name: 'Oriel', trait: 'Keen-eyed', counters: 'fog', blurb: 'Spots a gull at a mile in pea-soup fog.' },
  Brisk: { name: 'Brisk', trait: 'Beastwise', counters: 'beasts', blurb: 'Talks to boars. Sometimes they listen.' },
  Wren: { name: 'Wren', trait: 'Reef-reader', counters: 'reef', blurb: 'Knows every rock between here and Sunscar.' },
  Hollis: { name: 'Hollis', trait: 'Frostborn', counters: 'cold', blurb: 'Swims in winter, for fun.' },
};
/** Crew recruited by each Dispatch Logistics level. */
export const RECRUITS = ['Wren', 'Hollis'];

export interface MissionDef {
  id: string;
  name: string;
  description: string;
  minutes: number;
  hazards: Hazard[];
  /** Crew required. */
  crew: number;
  havenLevel: number;
  rewards: {
    items: { id: string; min: number; max: number }[];
    silver: [number, number];
    gold?: [number, number];
    card?: { chance: number; pool: string[] };
  };
}

export const MISSIONS: MissionDef[] = [
  {
    id: 'herb_run', name: 'Coastal Herb Run', minutes: 10, hazards: ['fog'], crew: 1, havenLevel: 1,
    description: 'Comb the misty headlands for wild herbs.',
    rewards: { items: [{ id: 'herb', min: 8, max: 12 }, { id: 'herb_rare', min: 0, max: 1 }], silver: [120, 180] },
  },
  {
    id: 'timber_haul', name: 'Driftwood Haul', minutes: 15, hazards: ['storm'], crew: 1, havenLevel: 1,
    description: 'Storms wash good timber onto the northern shoals.',
    rewards: { items: [{ id: 'timber', min: 8, max: 12 }, { id: 'timber_rare', min: 0, max: 1 }], silver: [160, 240] },
  },
  {
    id: 'reef_salvage', name: 'Reef Salvage', minutes: 25, hazards: ['reef', 'storm'], crew: 2, havenLevel: 2,
    description: 'A merchant cog broke on the Teeth. Its hold is still full.',
    rewards: { items: [{ id: 'relic_fragment', min: 6, max: 10 }, { id: 'ore_raw', min: 5, max: 8 }, { id: 'ore_rare', min: 0, max: 1 }], silver: [320, 480] },
  },
  {
    id: 'beast_trail', name: 'Beast Trail Survey', minutes: 30, hazards: ['beasts', 'fog'], crew: 2, havenLevel: 2,
    description: 'Map the game trails of the wild isle and bring back pelts.',
    rewards: { items: [{ id: 'hide', min: 8, max: 12 }, { id: 'fish', min: 4, max: 6 }], silver: [400, 600], card: { chance: 0.12, pool: ['c_lyra', 'c_bjorn', 'c_hadi', 'c_lio'] } },
  },
  {
    id: 'frost_charter', name: 'Frostline Charter', minutes: 45, hazards: ['cold', 'storm'], crew: 2, havenLevel: 4,
    description: 'Ferry supplies to the abbey and fish the ice shelf on the way back.',
    rewards: { items: [{ id: 'fish_rare', min: 1, max: 2 }, { id: 'ore_rare', min: 1, max: 2 }, { id: 'fish', min: 6, max: 10 }], silver: [700, 1000] },
  },
  {
    id: 'colossus_scouting', name: 'Colossus Scouting', minutes: 60, hazards: ['beasts', 'cold', 'reef'], crew: 3, havenLevel: 5,
    description: 'Track a colossus to its lair and chart its habits.',
    rewards: { items: [{ id: 'relic_rare', min: 1, max: 1 }, { id: 'hide', min: 10, max: 14 }], silver: [1200, 1600], gold: [3, 6], card: { chance: 0.3, pool: ['c_cinderhide', 'c_gloomfang', 'c_rimebound', 'c_dunestrider'] } },
  },
];

export const MISSION_BY_ID: Record<string, MissionDef> = Object.fromEntries(MISSIONS.map((m) => [m.id, m]));

// ================================================================ garden

export interface CropDef {
  id: string;
  name: string;
  minutes: number;
  /** Silver for seeds. */
  seeds: number;
  yields: { id: string; count: number }[];
  havenLevel: number;
  hue: number;
}

export const CROPS: CropDef[] = [
  { id: 'herb', name: 'Wild Herb Beds', minutes: 15, seeds: 60, yields: [{ id: 'herb', count: 8 }], havenLevel: 1, hue: 0x7bd66b },
  { id: 'moonpetal', name: 'Moonpetal Trellis', minutes: 40, seeds: 200, yields: [{ id: 'herb', count: 4 }, { id: 'herb_rare', count: 2 }], havenLevel: 2, hue: 0xa0c0ff },
  { id: 'lanternbloom', name: 'Lanternbloom Grove', minutes: 90, seeds: 450, yields: [{ id: 'herb', count: 10 }, { id: 'herb_rare', count: 4 }], havenLevel: 4, hue: 0xffd070 },
];
export const CROP_BY_ID: Record<string, CropDef> = Object.fromEntries(CROPS.map((c) => [c.id, c]));
export const GROWTH_STAGES = ['Seeded', 'Sprouting', 'Budding', 'Ready to harvest'];

// ================================================================ Haven level

/** Cumulative Haven XP needed to reach level n (index n - 1). */
export const HAVEN_LEVEL_XP = [0, 80, 220, 420, 700, 1050, 1500, 2050, 2700, 3500];
export const HAVEN_MAX_LEVEL = HAVEN_LEVEL_XP.length;

export const XP = {
  research: (minutes: number) => 30 + minutes * 5,
  craftBatch: 4,
  dispatch: (minutes: number) => 10 + minutes,
  harvest: (minutes: number) => 15 + Math.round(minutes / 3),
};
